<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: 'Engagement' },
      { label: $t('ui.entities.comms.call', 2), to: '/reports/engagement/calls' }
    ]"
  >
    <template #right>
      <div class="flex items-center gap-2">
        <Select
          v-model="callCenterSelected"
          :options="callCentersAllowed"
          icon="headset"
          value-key="id"
          label-key="name"
          :placeholder="`Select a ${$t('ui.entities.departments.callCenter')}`"
          class="min-w-56"
          :readonly="callCentersAllowed.length == 1"
          @select="updateCallCenterSelected"
        ></Select>

        <DatePicker
          v-model="dateRange"
          mode="week"
          :max-date="dayjs().format('YYYY-MM-DD')"
        />
        <div>
          <Button
            label="Update"
            :is-loading="isLoading"
            @click="onFetchData"
          />
        </div>
      </div>
    </template>
  </PageHeader>
  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Calls"
      :value="callsData"
      removable-sort
      sort-field="timestamp"
      :sort-order="-1"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :global-filter-fields="[
        'dealershipId',
        'dealershipName',
        'user.forename',
        'user.surname',
        'acceptedBy.name',
        'customer.name',
        'numberFrom',
        'numberTo',
        'tags'
      ]"
      :rows="20"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-report-engagement-calls"
      state-storage="session"
      :loading="isLoading"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
    >
      <template #empty>
        <Empty
          v-if="!isLoading"
          :title="callCenterSelected ? 'No data' : `Select a ${$t('ui.entities.departments.callCenter')}`"
        />
      </template>

      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV($event)"
            />
          </span>
        </div>
      </template>

      <Column
        field="dealershipName"
        header="Dealership"
        hidden
      ></Column>
      <Column
        field="dealershipId"
        header="Dealership"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <div class="flex items-center gap-3">
            <Avatar
              v-if="data.dealershipId"
              :url="data.brandLogo"
              is-logo
            />
            <Avatar
              v-else
              icon="dash"
            />
            <div>
              <div class="font-semibold">
                {{ data.dealershipName || '-' }}
              </div>
              <div class="text-sm text-quaternary">
                {{ data.direction == 'outbound' ? data.numberFrom : data.numberTo }}
              </div>
            </div>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="dealershipsList"
            mode="multiple"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Any"
            class="min-w-64"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>
      <Column
        field="direction"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <Icon
            v-if="data.direction == 'inbound'"
            type="arrow-left"
          />
          <Icon
            v-if="data.direction == 'outbound'"
            type="arrow-right"
          />
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="[
              { value: 'inbound', label: 'Inbound' },
              { value: 'outbound', label: 'Outbound' }
            ]"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Any"
            class="min-w-56"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>
      <Column
        field="customer.name"
        header="Customer"
        :sortable="true"
      >
        <template #body="{ data }">
          <router-link
            :to="data.customer.agreementId ? '/agreement/' + data.customer.agreementId : ''"
            class="link"
          >
            <div class="flex items-center gap-3">
              <Avatar :icon="data.customer.agreementId ? 'user' : 'dash'" />
              <div>
                <div
                  v-if="data.customer.agreementId"
                  class="font-semibold whitespace-nowrap"
                >
                  {{ data.customer.name || '-' }}
                </div>
                <div
                  v-else
                  class="mb-2"
                >
                  <Dropdown>
                    <template #triggerContent>
                      <Button
                        label="Link to customer"
                        size="xs"
                        outlined
                      />
                    </template>
                    <Select
                      v-model="selectedAgreement"
                      v-model:search-term="searchTerm"
                      placeholder="Search for customer"
                      :options="agreementsList"
                      value-key="agreementId"
                      label-key="name"
                      searchable
                      :handle-search="false"
                      on-select-return-as-object
                      class="px-2 min-w-64"
                      @select="linkToCustomer(data, $event)"
                    >
                      <template #option="{ option }">
                        <div class="flex flex-col w-full text-wrap">
                          <div>{{ option.name }}</div>
                          <div class="text-sm text-tertiary">
                            [{{ option.vrm }}] {{ option.vehicleMake }} {{ option.vehicleModel }}
                          </div>
                        </div>
                      </template>
                    </Select>
                  </Dropdown>
                </div>

                <div class="text-sm text-quaternary">
                  {{ data.direction == 'outbound' ? data.numberTo : data.numberFrom }}
                </div>
              </div>
            </div>
          </router-link>
        </template>
      </Column>

      <Column
        field="user.forename"
        header="User"
        :sortable="true"
      >
        <template #body="{ data }">
          <div
            v-if="data.user"
            class="flex items-center gap-3"
          >
            <Avatar
              :url="data.user.profilePicUrl || null"
              :text="data.user.initials || null"
              :icon="!data.user.profilePicUrl ? 'user' : null"
            />
            <div class="font-semibold whitespace-nowrap">
              {{ `${data.user.forename || ''} ${data.user.surname || ''}`.trim() || '-' }}
            </div>
          </div>
          <div v-else>-</div>
        </template>
      </Column>

      <Column
        field="numberFrom"
        header="From"
        hidden
        :sortable="true"
      ></Column>
      <Column
        field="numberTo"
        header="To"
        hidden
        :sortable="true"
      ></Column>

      <Column
        field="reason"
        header="Reason"
      >
        <template #body="{ data }">
          <Chip
            v-for="reason in data.reason"
            :key="reason"
            size="sm"
            class="mr-2"
          >
            <div class="text-sm">{{ reason }}</div>
          </Chip>
        </template>
      </Column>

      <Column
        field="answeredBy"
        header="Answered By"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="flex gap-2">
            <div
              v-if="data.answeredBy"
              class="flex gap-2"
            >
              <Avatar
                v-tippy="{ content: data.answeredBy == 'machine' ? 'Voicemail' : 'Customer' }"
                :icon="data.answeredBy == 'machine' ? 'voicemail' : 'user'"
              />
            </div>
            <AudioPlayer
              v-if="data.recordingUrl"
              :audio-src="data.recordingUrl"
              compact
            />
          </div>
        </template>
      </Column>

      <Column
        field="duration"
        header="Length"
        :sortable="true"
      >
        <template #body="{ data }">
          <div class="flex gap-2 items-center">
            <div>{{ $dayjs.duration(data.duration, 'seconds').format('mm:ss') }}</div>
          </div>
        </template>
      </Column>

      <Column
        field="acceptedAt"
        header="Accepted"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <div
            v-if="data.acceptedBy || data.acceptedAt"
            class="flex gap-2 items-center"
          >
            <Chip
              v-if="data.acceptedBy"
              v-tippy="'Accepted by'"
              :avatar="
                data.acceptedBy?.profilePicUrl || $cdnBaseUrl + '/img/profile-pics/' + data.acceptedBy?.profilePic
              "
              :avatar-text="data.acceptedBy?.initials"
              :label="data.acceptedBy?.name || data.acceptedBy?.fullName"
              size="md"
            />
            <Chip
              v-if="data.acceptedAt"
              v-tippy="'Accepted at'"
              icon="clock"
              :label="$dayjs(data.acceptedAt).format('HH:mm')"
              size="md"
            />
          </div>
          <div v-else>-</div>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="usersList"
            mode="multiple"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Any"
            class="min-w-56"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        field="tags"
        header="Tags"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <div class="flex gap-2 items-center">
            <ChipList :items="formatTags(data.tags)" />
          </div>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="[
              'Left Message',
              'Decided',
              'Unaware of Options',
              'Undecided',
              'Confused',
              'Dubious',
              'Going Elsewhere',
              'Disappointed',
              'Complaint',
              'Refused Data Protection',
              'Booked Appointment',
              'Appointment Query',
              'Appointment Complaint',
              'Inconvenient Time',
              'Connection Issues',
              'Incorrect Number',
              'Call Failed',
              'Phone System Error',
              'Unable to Complete DPQ',
              'Answered by Other Party',
              'Answered by Relative'
            ]"
            mode="multiple"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Any"
            class="min-w-56"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column v-if="$can('manage_conversations')">
        <template #body="{ data }">
          <Dropdown>
            <template #triggerContent>
              <Button
                icon="ellipsis-vertical"
                plain
                severity="secondary"
              ></Button>
            </template>
            <DropdownItem @item-clicked="deleteCall(data.id)">
              <Icon
                type="trash"
                class="text-error"
              />
              <span class="w-full">{{ $t('ui.actions.delete') }}</span>
            </DropdownItem>
          </Dropdown>
        </template>
      </Column>
    </DataTable>
  </Card>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

import Card from '@/components/card/Card.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import Input from '@/components/forms/Input.vue'
import Button from '@/components/button/Button.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Icon from '@/components/icon/Icon.vue'
import Chip from '@/components/chip/Chip.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
import AudioPlayer from '@/components/audio-player/AudioPlayer.vue'
import Select from '@/components/forms/Select.vue'
import Empty from '@/components/empty/Empty.vue'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { useCallCenterStore } from '@/stores/CallCenterStore.ts'
import { alertToast } from '@/utilities/notification'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode, FilterService } from '@primevue/core/api'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import ChipList from '@/components/chip/ChipList.vue'
import useMeilisearch from '@/composables/useMeilisearch'

const dt = ref()
const authStore = useAuthStore()
const callCenterStore = useCallCenterStore()
const dealershipsAllowed = ref(authStore.accessAllowed.dealerships)
const dateRange = ref({ start: dayjs().startOf('week').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') })
const callsApiRequest = useApiRequest()
const deleteCallApiRequest = useApiRequest()
const linkAgreementApiRequest = useApiRequest()
const isLoading = callsApiRequest.isLoading

const callCentersAllowed = callCenterStore.callCenters
const callCenterSelected = ref(null)

const callsData = ref([])
const users = ref([])
const filters = ref([])

const dealershipIDs = authStore.accessAllowed.dealerships.map(dealership => dealership.id)
const dealershipIDsString = dealershipIDs.join(',')
const selectedAgreement = ref(null)
const searchTerm = ref('')
const meiliSearchResults = ref([])
const { createSearch } = useMeilisearch()
const { scheduleSearch, dispose: disposeSearch } = createSearch(
  async ({ client, signal, query }) => {
    return client.index('agreements').search(
      query,
      {
        filter: 'dealershipId IN [' + dealershipIDsString + ']'
      },
      { signal }
    )
  },
  {
    debounceMs: 300,
    onClear: () => {
      meiliSearchResults.value = []
    },
    onResult: results => {
      meiliSearchResults.value = results
    }
  }
)

// Create a computed property to transform the data
const agreementsList = computed(() => {
  if (meiliSearchResults.value.hits != undefined) {
    return meiliSearchResults.value.hits
  }
  return false
})

watch(searchTerm, newSearchTerm => {
  scheduleSearch(newSearchTerm)
})

onUnmounted(() => {
  disposeSearch()
})

onMounted(() => {
  FilterService.register('tagsFilter', (value, filter) => {
    if (!filter || !filter.length) return true
    if (!Array.isArray(value)) return false

    return value.some(tag => filter.includes(tag))
  })
})

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dealershipId: { value: null, matchMode: FilterMatchMode.IN },
    'acceptedBy.id': { value: null, matchMode: FilterMatchMode.IN },
    wasDelivered: { value: null, matchMode: FilterMatchMode.IN },
    tags: { value: null, matchMode: 'tagsFilter' },
    direction: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  initFilters()
}

function fetchCallsData() {
  if (!callCenterSelected.value) {
    return
  }
  callsApiRequest
    .send({
      endpoint: '/v1/comms/calls?',
      params: {
        callCenterId: callCenterSelected.value,
        start: dayjs(dateRange.value.start).format('YYYY-MM-DD'),
        end: dayjs(dateRange.value.end).format('YYYY-MM-DD')
      }
    })
    .then(response => {
      callsData.value = response.data
    })
}

const formatTags = tags => {
  const formatted = []
  tags?.forEach(tag => {
    if (tag.length) {
      formatted.push({ label: tag, size: 'sm' })
    }
  })
  return formatted
}

if (!localStorage.getItem('report-filter-call-center-selected')) {
  callCenterSelected.value = callCentersAllowed[0]?.id
} else {
  callCenterSelected.value = parseInt(localStorage.getItem('report-filter-call-center-selected'))
}

fetchCallsData()

function onFetchData() {
  if (dateRange.value.start && dateRange.value.end && callCenterSelected.value) {
    fetchCallsData()
  }
}

function updateCallCenterSelected() {
  localStorage.setItem('report-filter-call-center-selected', callCenterSelected.value)
}

function deleteCall(id) {
  Swal.fire({
    title: 'Are you sure?',
    text: 'This action cannot be undone - not even by an admin!',
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes, delete this call',
    confirmButtonColor: 'var(--bg-error-solid)',
    cancelButtonText: 'Cancel'
  }).then(function (swalResponse) {
    if (swalResponse.isConfirmed && id) {
      try {
        deleteCallApiRequest.send({ endpoint: `/v1/comms/calls/${id}`, method: 'DELETE' }).then(response => {
          if (response.success) {
            alertToast('Call deleted', null, 'success')

            // Remove call from table data
            const index = callsData.value.indexOf(call => call.id == id)
            if (index) {
              callsData.value.splice(index, 1)
            }
          } else {
            alertToast('Failed to delete call', response.message, 'error')
          }
        })
      } catch (err) {
        alertToast('Failed to delete call', err.message, 'error')
      }
    }
  })
}

// Users List
const usersApiRequest = useApiRequest()
usersApiRequest.send({ endpoint: '/v1/users?userType=retain' }).then(response => {
  if (response.http.status == 200) {
    users.value = response.data
  }
})

const usersList = computed(() => {
  // if no users, return empty array
  if (!users.value) return []

  return users.value.map(u => {
    return {
      value: u.id,
      label: u.fullName,
      profilePicUrl: u.profilePicUrl,
      initials: u.initials
    }
  })
})

const dealershipsList = computed(() => {
  return dealershipsAllowed.value.map(d => {
    return {
      value: d.id,
      label: d.name
    }
  })
})

function linkToCustomer(comm, $event) {
  if (comm?.id && $event.agreementId) {
    try {
      linkAgreementApiRequest
        .send({
          endpoint: '/v1/comms/calls/' + comm.id,
          data: {
            agreementId: parseInt($event.agreementId)
          },
          method: 'PATCH'
        })
        .then(response => {
          if (response.success) {
            alertToast('Customer linked', null, 'success')

            comm.customer.agreementId = $event.agreementId
            comm.customer.name = $event.name
          } else {
            alertToast('Failed to link customer', response.message, 'error')
          }
        })
    } catch (err) {
      alertToast('Failed to link customer', err.message, 'error')
    }
  } else {
    alertToast('No agreement selected', 'Please select an agreement to link to this call', 'info')
  }
}

const exportCSV = () => {
  dt.value.exportCSV()
}
</script>

<style></style>
